/**
 * WorkspaceReadView — Read mode content rendering with sentencing overlays
 *
 * Extracted from MainWorkspace to reduce component size.
 * Handles: article rendering, thread outline sidebar, sentencing overlays,
 * text highlighting, media/conversation link click handling, and
 * cross-source related thread discovery.
 *
 * @module @humanizer/studio/components/workspace/WorkspaceReadView
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MathMarkdown, tryParseJson } from '../markdown';
import { JsonRenderer } from '../markdown';
import { ThreadOutline, type RelatedThread } from './ThreadOutline';
import { ImageDetailPopover } from './ImageDetailPopover';
import { usePanels } from '../../contexts/PanelContext';
import { useMediaApi } from '../../hooks/useMediaApi';
import { extractPointerFromSrc } from '../../utils/media-url';
import type { ContentItem } from '../../contexts/BufferSyncContext';
import type { WorkspaceContent } from './MainWorkspace';
import type { SentenceStepResult, TetralemmaReading, SentencingV2Result, SentencingV2Sentence, SentenceRole } from '../../types/analysis';
import { escapeHtml } from '../../utils/html-escape';
import { useArchiveApi } from '../../hooks/useArchiveApi';

/**
 * Derive a context badge from source metadata (platform-specific context)
 */
function getContextBadge(metadata?: Record<string, unknown>): { icon: string; label: string; detail?: string } | null {
  if (!metadata) return null;

  const fb = metadata.facebookContext as { participants?: Array<{ name: string }>; thread_type?: string; message_count?: number } | undefined;
  if (fb) {
    const count = fb.participants?.length ?? 0;
    const contextType = metadata.contextType as string | undefined;
    if (contextType === 'facebook-group-chat') {
      return { icon: '\u{1F465}', label: 'Group Chat', detail: count > 0 ? `${count} participants` : undefined };
    }
    if (contextType === 'facebook-dm') {
      const otherName = fb.participants?.find(p => p.name !== 'Facebook User')?.name;
      return { icon: '\u{1F4AC}', label: 'Messenger DM', detail: otherName || (count > 0 ? `${count} participants` : undefined) };
    }
    return { icon: '\u{1F4AC}', label: 'Messenger', detail: count > 0 ? `${count} participants` : undefined };
  }

  const ig = metadata.instagramContext as { username?: string; message_count?: number } | undefined;
  if (ig) {
    return { icon: '\u{1F4F7}', label: 'Instagram DM', detail: ig.username || undefined };
  }

  const rd = metadata.redditContext as { subreddit?: string } | undefined;
  if (rd) {
    return { icon: '\u{1F4E2}', label: rd.subreddit ? `r/${rd.subreddit}` : 'Reddit', detail: undefined };
  }

  const tw = metadata.twitterContext as { username?: string } | undefined;
  if (tw) {
    return { icon: '\u{1D54F}', label: tw.username ? `@${tw.username}` : 'Twitter', detail: undefined };
  }

  return null;
}

function ContextBadge({ metadata }: { metadata: Record<string, unknown> }) {
  const badge = getContextBadge(metadata);
  if (!badge) return null;
  return (
    <div className="workspace__context-badge">
      <span className="workspace__context-badge-icon">{badge.icon}</span>
      <span className="workspace__context-badge-label">{badge.label}</span>
      {badge.detail && (
        <span className="workspace__context-badge-detail">{badge.detail}</span>
      )}
    </div>
  );
}

export interface WorkspaceReadViewProps {
  isThreadMode: boolean;
  messages?: ContentItem[];
  currentMessage: ContentItem | null;
  activeMessageIndex: number;
  filteredIndices: number[];
  onJumpToMessage: (index: number) => void;
  displayText: string;
  displayRole: string | undefined;
  content: WorkspaceContent;
  highlightText?: string | null;
  sentencingData?: SentenceStepResult['step'][];
  roleClassifierData?: SentencingV2Result | null;
  onConversationClick?: (conversationId: string) => void;
  onMediaClick?: (src: string) => void;
  onAddMessageToStack: () => void;
}

export function WorkspaceReadView({
  isThreadMode,
  messages,
  currentMessage,
  activeMessageIndex,
  filteredIndices,
  onJumpToMessage,
  displayText,
  displayRole,
  content,
  highlightText,
  sentencingData,
  roleClassifierData,
  onConversationClick,
  onMediaClick,
  onAddMessageToStack,
}: WorkspaceReadViewProps) {
  const articleRef = useRef<HTMLElement>(null);
  const archiveApi = useArchiveApi();

  // Related threads (M4C cross-source discovery)
  const [relatedThreads, setRelatedThreads] = useState<RelatedThread[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const currentSourceType = isThreadMode ? messages?.[0]?.metadata.source?.type : undefined;
  const threadId = isThreadMode ? messages?.[0]?.metadata.source?.threadId : undefined;

  useEffect(() => {
    if (!isThreadMode || !messages?.length) {
      setRelatedThreads([]);
      return;
    }
    // Use first user message text (or first message) as the query
    const queryMsg = messages.find(m => m.metadata.authorRole === 'user') ?? messages[0];
    const queryText = queryMsg.text.slice(0, 500);
    if (!queryText.trim()) return;

    let cancelled = false;
    setRelatedLoading(true);
    archiveApi.findSimilar(queryText, { limit: 10, threshold: 0.3 })
      .then(({ results }) => {
        if (cancelled) return;
        // Filter: different source type + different thread + deduplicate by threadRootId
        const seen = new Set<string>();
        if (threadId) seen.add(threadId);
        const related: RelatedThread[] = [];
        for (const r of results) {
          const rtId = r.provenance?.threadRootId;
          if (!rtId || seen.has(rtId)) continue;
          // Cross-source: exclude same source type
          const rSourceType = r.provenance?.sourceType ?? r.sourceType ?? '';
          if (currentSourceType && rSourceType === currentSourceType) continue;
          seen.add(rtId);
          related.push({
            id: r.id,
            title: r.provenance?.threadTitle ?? r.title ?? '',
            sourceType: rSourceType,
            score: r.score,
            preview: r.text.slice(0, 120),
            threadRootId: rtId,
          });
          if (related.length >= 5) break;
        }
        setRelatedThreads(related);
      })
      .catch(() => { if (!cancelled) setRelatedThreads([]); })
      .finally(() => { if (!cancelled) setRelatedLoading(false); });

    return () => { cancelled = true; };
  }, [isThreadMode, threadId, currentSourceType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Highlight text in rendered content and scroll to it
  useEffect(() => {
    if (!highlightText || !articleRef.current) return;

    const timeout = setTimeout(() => {
      const container = articleRef.current;
      if (!container) return;

      container.querySelectorAll('mark.workspace__highlight').forEach((mark) => {
        const parent = mark.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
          parent.normalize();
        }
      });

      const searchText = highlightText.slice(0, 150);
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node: Text | null;
      let found = false;

      while ((node = walker.nextNode() as Text | null)) {
        const idx = node.textContent?.indexOf(searchText.slice(0, 60)) ?? -1;
        if (idx !== -1 && node.textContent) {
          const matchLen = Math.min(searchText.length, node.textContent.length - idx);
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, idx + matchLen);

          const mark = document.createElement('mark');
          mark.className = 'workspace__highlight';
          try {
            range.surroundContents(mark);
          } catch {
            // Range spans element boundary — skip highlight
            break;
          }

          mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
          found = true;
          break;
        }
      }

      if (found) {
        setTimeout(() => {
          container.querySelectorAll('mark.workspace__highlight').forEach((mark) => {
            const parent = mark.parentNode;
            if (parent) {
              parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
              parent.normalize();
            }
          });
        }, 10000);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [highlightText]);

  // Clear highlights when content changes
  useEffect(() => {
    if (articleRef.current) {
      articleRef.current.querySelectorAll('mark.workspace__highlight').forEach((mark) => {
        const parent = mark.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
          parent.normalize();
        }
      });
    }
  }, [content?.id, activeMessageIndex]);

  // Sentencing overlay
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const sentencingCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const container = articleRef.current;
    if (!container) return;

    container.querySelectorAll('span.sentence-reading').forEach((span) => {
      const parent = span.parentNode;
      if (parent) {
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
        parent.normalize();
      }
    });

    if (tooltipRef.current) {
      tooltipRef.current.remove();
      tooltipRef.current = null;
    }

    if (!sentencingData || sentencingData.length === 0) return;

    const CORNERS = ['literal', 'metaphorical', 'both', 'neither'] as const;
    const CORNER_LABELS: Record<string, string> = {
      literal: 'literal', metaphorical: 'metaphor', both: 'both', neither: 'neither',
    };

    function getDominant(reading: TetralemmaReading): typeof CORNERS[number] {
      let best: typeof CORNERS[number] = 'literal';
      let bestP = 0;
      for (const c of CORNERS) {
        if (reading[c].probability > bestP) { bestP = reading[c].probability; best = c; }
      }
      return best;
    }

    const timeout = setTimeout(() => {
      const steps = sentencingData.filter(Boolean);

      for (let idx = 0; idx < steps.length; idx++) {
        const step = steps[idx];
        if (!step) continue;
        const sentence = step.sentence;
        if (!sentence || sentence.length < 3) continue;

        const searchStr = sentence.slice(0, 80);
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        let node: Text | null;

        while ((node = walker.nextNode() as Text | null)) {
          const pos = node.textContent?.indexOf(searchStr) ?? -1;
          if (pos === -1 || !node.textContent) continue;

          if (node.parentElement?.classList.contains('sentence-reading')) break;

          const matchLen = Math.min(sentence.length, node.textContent.length - pos);
          const range = document.createRange();
          range.setStart(node, pos);
          range.setEnd(node, pos + matchLen);

          const span = document.createElement('span');
          span.className = 'sentence-reading';
          span.dataset.idx = String(idx);

          const reading = step.measurement?.reading;
          if (reading) {
            const dominant = getDominant(reading);
            span.classList.add(`sentence-reading--${dominant}`);
            span.dataset.reading = JSON.stringify(reading);
            span.dataset.rho = String(step.rhoDistance);
            span.dataset.total = String(steps.length);
          }

          try {
            range.surroundContents(span);
          } catch {
            // Range spans element boundary — skip this sentence
            break;
          }
          break;
        }
      }

      const tooltip = document.createElement('div');
      tooltip.className = 'sentence-tooltip';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;

      const handleMouseOver = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.sentence-reading') as HTMLElement | null;
        if (!target || !target.dataset.reading) return;

        let reading: TetralemmaReading;
        try {
          reading = JSON.parse(target.dataset.reading);
        } catch {
          return;
        }
        const rho = parseFloat(target.dataset.rho || '0');
        const stepIdx = parseInt(target.dataset.idx || '0', 10);
        const total = parseInt(target.dataset.total || '0', 10);
        const dominant = getDominant(reading);

        tooltip.innerHTML = `
          <div class="sentence-tooltip__header">Sentence ${stepIdx + 1} of ${total}</div>
          <div class="sentence-tooltip__bars">
            ${CORNERS.map(c => {
              const pct = (reading[c].probability * 100).toFixed(0);
              return `<div class="sentence-tooltip__bar-row">
                <div class="sentence-tooltip__bar-track">
                  <div class="sentence-tooltip__bar-fill sentence-tooltip__bar-fill--${c}" style="width:${pct}%"></div>
                </div>
                <span class="sentence-tooltip__bar-label">${CORNER_LABELS[c]}</span>
                <span class="sentence-tooltip__bar-pct">${pct}%</span>
              </div>`;
            }).join('')}
          </div>
          <div class="sentence-tooltip__evidence">${escapeHtml(reading[dominant].evidence)}</div>
          <div class="sentence-tooltip__rho">State shift: ${rho.toFixed(3)}</div>
        `;
        tooltip.style.display = 'block';

        const rect = target.getBoundingClientRect();
        tooltip.style.left = `${rect.left + rect.width / 2}px`;
        tooltip.style.top = `${rect.bottom + 8}px`;
      };

      const handleMouseOut = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.sentence-reading');
        if (target) tooltip.style.display = 'none';
      };

      container.addEventListener('mouseover', handleMouseOver);
      container.addEventListener('mouseout', handleMouseOut);

      sentencingCleanupRef.current = () => {
        container.removeEventListener('mouseover', handleMouseOver);
        container.removeEventListener('mouseout', handleMouseOut);
      };
    }, 400);

    return () => {
      clearTimeout(timeout);
      if (sentencingCleanupRef.current) {
        sentencingCleanupRef.current();
        sentencingCleanupRef.current = null;
      }
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [sentencingData, content?.id]);

  // ─── Role Classifier (v2) overlay ─────────────────────────────────────
  const roleTooltipRef = useRef<HTMLDivElement | null>(null);
  const roleCleanupRef = useRef<(() => void) | null>(null);

  const ROLE_DESCRIPTIONS: Record<SentenceRole, string> = {
    anchor: 'Load-bearing sentence. Establishes core claim.',
    advance: 'Moves the argument forward. Adds new ground.',
    ground: 'Supporting evidence. Concrete detail or example.',
    pivot: 'Direction change. Reframes or redirects.',
    echo: 'Reinforces a prior anchor. Builds resonance.',
    bridge: 'Connects disparate ideas. Transitional.',
    scaffold: 'Structural filler. May be removable.',
  };

  useEffect(() => {
    const container = articleRef.current;
    if (!container) return;

    // Clean up any existing role overlays
    container.querySelectorAll('span.sentence-role').forEach((span) => {
      const parent = span.parentNode;
      if (parent) {
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
        parent.normalize();
      }
    });

    if (roleTooltipRef.current) {
      roleTooltipRef.current.remove();
      roleTooltipRef.current = null;
    }

    if (!roleClassifierData || roleClassifierData.sentences.length === 0) return;

    const timeout = setTimeout(() => {
      const sentences = roleClassifierData.sentences;

      for (let idx = 0; idx < sentences.length; idx++) {
        const s = sentences[idx];
        if (!s.text || s.text.length < 3) continue;

        const searchStr = s.text.slice(0, 80);
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        let node: Text | null;

        while ((node = walker.nextNode() as Text | null)) {
          const pos = node.textContent?.indexOf(searchStr) ?? -1;
          if (pos === -1 || !node.textContent) continue;

          if (node.parentElement?.classList.contains('sentence-role')) break;

          const matchLen = Math.min(s.text.length, node.textContent.length - pos);
          const range = document.createRange();
          range.setStart(node, pos);
          range.setEnd(node, pos + matchLen);

          const span = document.createElement('span');
          span.className = `sentence-role sentence-role--${s.role}`;
          span.dataset.role = s.role;
          span.dataset.confidence = String(s.confidence);
          span.dataset.flags = s.flags.join(',');
          span.dataset.traceDistance = String(s.readerState.traceDistance);
          span.dataset.sicScore = String(s.constraintSignals.sicScore);
          span.dataset.idx = String(idx);
          span.dataset.total = String(sentences.length);

          try {
            range.surroundContents(span);
          } catch {
            break;
          }
          break;
        }
      }

      // Create tooltip
      const tooltip = document.createElement('div');
      tooltip.className = 'sentence-role-tooltip';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      roleTooltipRef.current = tooltip;

      const handleMouseOver = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.sentence-role') as HTMLElement | null;
        if (!target || !target.dataset.role) return;

        const role = target.dataset.role as SentenceRole;
        const confidence = parseFloat(target.dataset.confidence || '0');
        const flags = target.dataset.flags ? target.dataset.flags.split(',').filter(Boolean) : [];
        const traceDistance = parseFloat(target.dataset.traceDistance || '0');
        const sicScore = parseFloat(target.dataset.sicScore || '0');
        const pct = (confidence * 100).toFixed(0);
        const barWidth = Math.round(confidence * 100);

        const flagsHtml = flags.length > 0
          ? `<div class="sentence-role-tooltip__flags">${flags.map(f => `<span class="sentence-role-tooltip__flag sentence-role-tooltip__flag--${f}">${f}</span>`).join('')}</div>`
          : '';

        tooltip.innerHTML = `
          <div class="sentence-role-tooltip__header">
            <span class="sentence-role-tooltip__role-name sentence-role-tooltip__role-name--${role}">${role.toUpperCase()}</span>
            <span class="sentence-role-tooltip__confidence-value">${pct}%</span>
          </div>
          <div class="sentence-role-tooltip__bar-track">
            <div class="sentence-role-tooltip__bar-fill sentence-role-tooltip__bar-fill--${role}" style="width:${barWidth}%"></div>
          </div>
          ${flagsHtml}
          <div class="sentence-role-tooltip__metrics">
            <span>Trace: ${traceDistance.toFixed(3)}</span>
            <span>SIC: ${sicScore.toFixed(2)}</span>
          </div>
          <div class="sentence-role-tooltip__description">${escapeHtml(ROLE_DESCRIPTIONS[role] ?? '')}</div>
        `;
        tooltip.style.display = 'block';

        const rect = target.getBoundingClientRect();
        tooltip.style.left = `${rect.left + rect.width / 2}px`;
        tooltip.style.top = `${rect.bottom + 8}px`;
      };

      const handleMouseOut = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest('.sentence-role');
        if (target) tooltip.style.display = 'none';
      };

      container.addEventListener('mouseover', handleMouseOver);
      container.addEventListener('mouseout', handleMouseOut);

      roleCleanupRef.current = () => {
        container.removeEventListener('mouseover', handleMouseOver);
        container.removeEventListener('mouseout', handleMouseOut);
      };
    }, 400);

    return () => {
      clearTimeout(timeout);
      if (roleCleanupRef.current) {
        roleCleanupRef.current();
        roleCleanupRef.current = null;
      }
      if (roleTooltipRef.current) {
        roleTooltipRef.current.remove();
        roleTooltipRef.current = null;
      }
    };
  }, [roleClassifierData, content?.id]);

  // ─── Image hover popover ────────────────────────────────────────────────
  const { setPendingMediaId } = usePanels();
  const mediaApi = useMediaApi();
  const [hoveredImageSrc, setHoveredImageSrc] = useState<string | null>(null);
  const [hoveredImageRect, setHoveredImageRect] = useState<DOMRect | null>(null);
  const imageHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverMouseInsideRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = articleRef.current;
    if (!container) return;

    const handleMouseOver = (e: MouseEvent) => {
      const img = (e.target as HTMLElement).closest('img.md-image') as HTMLImageElement | null;
      if (!img) return;

      // Clear any pending dismiss
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }

      // Don't re-trigger for same image
      if (hoveredImageSrc === img.src) return;

      // Delay before showing
      if (imageHoverTimerRef.current) clearTimeout(imageHoverTimerRef.current);
      imageHoverTimerRef.current = setTimeout(() => {
        setHoveredImageSrc(img.src);
        setHoveredImageRect(img.getBoundingClientRect());
      }, 400);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const img = (e.target as HTMLElement).closest('img.md-image');
      if (!img) return;

      // Cancel pending show
      if (imageHoverTimerRef.current) {
        clearTimeout(imageHoverTimerRef.current);
        imageHoverTimerRef.current = null;
      }

      // Bridge delay — allow mouse to reach popover
      dismissTimerRef.current = setTimeout(() => {
        if (!popoverMouseInsideRef.current) {
          setHoveredImageSrc(null);
          setHoveredImageRect(null);
        }
      }, 150);
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      if (imageHoverTimerRef.current) clearTimeout(imageHoverTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [hoveredImageSrc]);

  const handlePopoverMouseEnter = useCallback(() => {
    popoverMouseInsideRef.current = true;
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const handlePopoverMouseLeave = useCallback(() => {
    popoverMouseInsideRef.current = false;
    dismissTimerRef.current = setTimeout(() => {
      setHoveredImageSrc(null);
      setHoveredImageRect(null);
    }, 150);
  }, []);

  const handleOpenInGallery = useCallback(() => {
    if (!hoveredImageSrc) return;
    const pointer = extractPointerFromSrc(hoveredImageSrc);
    if (!pointer) return;
    setHoveredImageSrc(null);
    setHoveredImageRect(null);
    mediaApi.byPointer(pointer).then(result => {
      if (result.found && result.media) {
        setPendingMediaId(result.media.id);
      }
    }).catch(() => {});
  }, [hoveredImageSrc, mediaApi, setPendingMediaId]);

  // Intercept clicks on media images and conversation links in rendered markdown
  const handleArticleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check for conversation link: href="#conversation:ID"
    const link = target.closest('a');
    if (link) {
      const href = link.getAttribute('href') || '';
      const convMatch = href.match(/^#conversation:(.+)$/);
      if (convMatch) {
        e.preventDefault();
        onConversationClick?.(convMatch[1]);
        return;
      }
    }

    // Check for media image click
    const img = target.closest('img.md-image') as HTMLImageElement | null;
    if (img) {
      e.preventDefault();
      // Dismiss hover popover on click
      setHoveredImageSrc(null);
      setHoveredImageRect(null);
      onMediaClick?.(img.src);
      return;
    }
  }, [onMediaClick, onConversationClick]);

  return (
    <div className={`workspace__content-area${isThreadMode ? ' workspace__content-area--thread' : ''}`}>
      {/* Thread outline sidebar */}
      {isThreadMode && messages && (
        <ThreadOutline
          messages={messages}
          activeIndex={activeMessageIndex}
          filteredIndices={filteredIndices}
          onJump={onJumpToMessage}
          relatedThreads={relatedThreads}
          relatedLoading={relatedLoading}
          onOpenRelated={onConversationClick}
        />
      )}

      {/* Article */}
      <article className="workspace__article" ref={articleRef} onClick={handleArticleClick}>
        {/* Context badge: platform source indicator (Messenger DM, Group Chat, etc.) */}
        {isThreadMode && !!messages?.[0]?.metadata?.sourceMetadata && (
          <ContextBadge metadata={messages[0].metadata.sourceMetadata as Record<string, unknown>} />
        )}
        {/* Thread message header: role + Stack button at the TOP so it's always visible */}
        {isThreadMode && currentMessage && (
          <div className="workspace__message-header">
            <span className="workspace__stat">
              {currentMessage.metadata.authorRole ?? 'unknown'}
            </span>
            <span className="workspace__stat">
              {(currentMessage.metadata.wordCount ?? 0).toLocaleString()} words
            </span>
            <button
              className="workspace__add-to-stack-btn"
              onClick={onAddMessageToStack}
              title="Add this message to the card stack"
            >
              + Stack
            </button>
          </div>
        )}
        {displayRole === 'tool' && (
          <span className="workspace__role-badge workspace__role-badge--tool">tool</span>
        )}
        {tryParseJson(displayText) !== null
          ? <JsonRenderer>{displayText}</JsonRenderer>
          : <MathMarkdown>{displayText}</MathMarkdown>
        }
        {!isThreadMode && content.metadata?.wordCount && (
          <div className="workspace__stats">
            <span className="workspace__stat">
              {content.metadata.wordCount.toLocaleString()} words
            </span>
            {content.metadata.authorRole && (
              <span className="workspace__stat">{content.metadata.authorRole}</span>
            )}
          </div>
        )}
      </article>

      {/* Image detail popover (hover) */}
      {hoveredImageSrc && hoveredImageRect && (
        <ImageDetailPopover
          imageSrc={hoveredImageSrc}
          imageRect={hoveredImageRect}
          onOpenConversation={(threadId) => {
            setHoveredImageSrc(null);
            setHoveredImageRect(null);
            onConversationClick?.(threadId);
          }}
          onOpenInGallery={handleOpenInGallery}
          onMouseEnterPopover={handlePopoverMouseEnter}
          onMouseLeavePopover={handlePopoverMouseLeave}
        />
      )}
    </div>
  );
}
